import React, { useState } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { toggleModal, toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtra } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import InputPasscode from 'apps/auth/components/common/InputPasscode/InputPasscode';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import PasskeyButton from 'apps/auth/components/passkey/PasskeyButton';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { FORGOT_PASSCODE_CLICKED, PASSCODE_SET_SHOWN, PASSCODE_SUCCESSFULLY_ENTERED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_COUNTRY, PAYOUT_COUNTRY } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { TOKEN_ID, USER_NAME, USER_TOKEN_PHONE } from 'apps/auth/constants/Constants';
import { SMS_CONFIRMATION } from 'apps/auth/constants/ExtraNames';
import { PASSCODE_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_SEND_MAIN_LINK, LOGIN } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import Cookies from 'universal-cookie';

import DownloadAppPopup from './DownloadAppPopup';
import ForgotYourPasscodePopup from './ForgotYourPasscodePopup';

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: ${(props) => (props.$isKeyboardOpened ? 'flex-start' : 'space-between')};
    flex: 1;
    position: relative;
`;
const ButtonsContainer = styled.div`
    margin-top: ${(props) => (props.$isKeyboardOpened ? '24px' : null)};
`;

const InsertPasscode = (props) => {
    const { createPasscode, authViaPasscode, redirectUrl, phoneCountry } = useExtra();
    const redirectTarget = redirectUrl || AUTH_SEND_MAIN_LINK;
    const messages = usePageMessages();
    const [errorMessage, setErrorMessage] = useState(null);
    const [isKeyboardOpened, setIsKeyboardOpened] = useState(false);

    const formName = PASSCODE_FORM_NAME;
    const ERROR_CODE_FOR_PASSCODE_TRY_AGAIN = 590;
    const afterPasscodeCreateHandler = (newField) => {
        props.toggleSpinner(true);
        apiFetchData('/create/passcode/for/auth', { passcode: { ...newField } })
            .then((json) => {
                const cookies = new Cookies();
                const expirationDate = new Date();
                expirationDate.setFullYear(expirationDate.getFullYear() + 1);
                cookies.set(TOKEN_ID, json.extra.tokenId, { path: '/', expires: expirationDate });
                cookies.set(USER_TOKEN_PHONE, json.extra.userTokenPhone, { path: '/', expires: expirationDate });
                cookies.set(USER_NAME, json.extra.name, { path: '/', expires: expirationDate });
                props.toggleSpinner(false).then(() =>
                    props.toggleModalWithTimer(true, messages.passcodeSetSpinner, () => {
                        amplitude.getInstance().logEvent(PASSCODE_SET_SHOWN, {
                            [PAYMENT_COUNTRY]: phoneCountry?.nameEn,
                        });
                        window.location.href = redirectUrl || AUTH_SEND_MAIN_LINK;
                    }),
                );
            })
            .catch((error) => {
                logWarn(error);
                props.toggleSpinner(false);
            });
    };

    const afterPasscodeAuthHandler = (newField) => {
        props.toggleSpinner(true);
        apiFetchData('/login/via/passcode', { passcode: { ...newField } })
            .then((json) => {
                if (json.errors.passcode) {
                    let passcodeError = json.errors.passcode;
                    if (passcodeError.code === ERROR_CODE_FOR_PASSCODE_TRY_AGAIN) {
                        setErrorMessage(passcodeError.body);
                        props.changeFormFieldValue(formName, '', 'passcode').then(() => {
                            props.toggleSpinner(false);
                        });
                    } else {
                        props.popupRef.current?.open(
                            <ForgotYourPasscodePopup
                                popupRef={props.popupRef}
                                buttonOnClick={deleteCookiesAndRedirectToLogin}
                                onPopupClose={deleteCookiesAndRedirectToLogin}
                            />,
                        );
                    }
                } else if (json?.extra?.showPreparedPs3MigrationScreen) {
                    props.toggleModal(
                        true,
                        <ModalComponent isVerticalCentered canCloseModal={false}>
                            <InfoPage
                                insideModalComponent
                                iconName="clock-80"
                                iconColor="primary"
                                primaryButtonCaption={json.extra.showMigrationChat ? json.messages?.contactSupport : json.messages?.gotIt}
                                headerText={json.messages?.preparingBetaTitle}
                                secondaryText={<DangerouslyHtml dangerouslyHtml={json.messages?.preparingBetaSubtitle} />}
                                primaryButtonOnClick={(e) => {
                                    e.preventDefault();
                                    if (json.extra.showMigrationChat) {
                                        props.toggleChat(true);
                                    } else {
                                        props.toggleModal(false);
                                    }
                                }}
                            />
                        </ModalComponent>,
                    );
                    props.toggleSpinner(false);
                    return;
                } else if (json?.extra?.webUzbekistanUsersDisabled) {
                    props.popupRef.current?.open(
                        <DownloadAppPopup
                            popupRef={props.popupRef}
                            messages={json.messages}
                            extra={json.extra}
                            onClick={() => {
                                deleteCookiesAndRedirectToLogin();
                            }}
                            onOverlayClick={() => {
                                deleteCookiesAndRedirectToLogin();
                            }}
                        />,
                    );
                    props.toggleSpinner(false);
                    return;
                } else if (json?.extra?.smsConfirmation?.objectType === 'VERIFY_KNOW_USER_INFO') {
                    Promise.all([props.addExtra(SMS_CONFIRMATION, json.extra.smsConfirmation), props.updatePageMessages(json.messages)]).then(() => {
                        amplitude.getInstance().logEvent(PASSCODE_SUCCESSFULLY_ENTERED, {
                            [PAYOUT_COUNTRY]: phoneCountry?.nameEn,
                        });
                        props.toggleSpinner(false);
                    });
                } else {
                    amplitude.getInstance().logEvent(PASSCODE_SUCCESSFULLY_ENTERED, {
                        [PAYOUT_COUNTRY]: phoneCountry?.nameEn,
                    });
                    window.location.href = json.redirectTo;
                }
            })
            .catch((error) => {
                props.toggleSpinner(false);
                logWarn(error);
            });
    };

    const afterPasscodeEnterHandler = (newField) => {
        createPasscode ? afterPasscodeCreateHandler(newField) : afterPasscodeAuthHandler(newField);
    };

    const forgotPasscodeClick = () => {
        amplitude.getInstance().logEvent(FORGOT_PASSCODE_CLICKED, {
            [PAYOUT_COUNTRY]: phoneCountry?.nameEn,
        });
        props.popupRef.current?.open(<ForgotYourPasscodePopup popupRef={props.popupRef} buttonOnClick={deleteCookiesAndRedirectToLogin} />);
    };

    const deleteCookiesAndRedirectToLogin = () => {
        props.toggleSpinner(true);
        props.popupRef?.current?.close();
        const cookies = new Cookies();
        const expiredDate = new Date();
        expiredDate.setFullYear(expiredDate.getFullYear() - 1);
        cookies.set(TOKEN_ID, '', { path: '/', expires: expiredDate });
        cookies.set(USER_NAME, '', { path: '/', expires: expiredDate });
        cookies.set(USER_TOKEN_PHONE, '', { path: '/', expires: expiredDate });
        window.location.replace(LOGIN);
    };

    const getHeaderPanel = () => {
        if (createPasscode) {
            return <HeaderPanel title={messages.setPasscodeTitle} subtitle={messages.setPasscodeSubTitle} indentSmall />;
        } else {
            return <HeaderPanel title={messages.helloTitle} subtitle={messages.enterPasscodeSubTitle} indentSmall />;
        }
    };

    const handleKeyboardToggle = (isOpened) => {
        setIsKeyboardOpened(isOpened);
    };

    return (
        <PageContainer $isKeyboardOpened={isKeyboardOpened}>
            {getHeaderPanel()}
            <InputPasscode
                formName={formName}
                afterPasscodeEnterHandler={afterPasscodeEnterHandler}
                maxLength={props.maxLength}
                errorMessage={errorMessage}
                removeMessageError={() => setErrorMessage('')}
                handleKeyboardToggle={handleKeyboardToggle}
            />
            <ButtonsContainer $isKeyboardOpened={isKeyboardOpened}>
                {createPasscode && messages.addPasskeyButton && (
                    <PasskeyButton mode="register" caption={messages.addPasskeyButton} redirectUrl={redirectTarget} toggleSpinner={props.toggleSpinner} />
                )}
                {authViaPasscode && (
                    <>
                        {messages.loginWithPasskeyButton && <PasskeyButton caption={messages.loginWithPasskeyButton} toggleSpinner={props.toggleSpinner} />}
                        <Button
                            variant="row"
                            textCenter
                            onClick={(e) => forgotPasscodeClick(e)}
                            caption={messages.forgotPasscodeButton}
                            id="forgot_passcode_link"
                        />
                        <Button
                            variant="row"
                            textCenter
                            onClick={(e) => deleteCookiesAndRedirectToLogin(e)}
                            caption={messages.itIsNotYouButton}
                            id="not_you_passcode_link"
                        />
                    </>
                )}
            </ButtonsContainer>
        </PageContainer>
    );
};

export default connect(null, {
    toggleSpinner,
    toggleModal,
    toggleChat,
    toggleModalWithTimer,
    changeFormFieldValue,
    addExtra,
    updatePageMessages,
})(InsertPasscode);
